"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { SendHorizontal, MessageCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { trackAction } from "@/lib/track-action";

const WHATSAPP_NUMBER = "5218125707844";

type Message = { role: "user" | "assistant"; content: string };

const SUGGESTED_PROMPTS = [
  "Monitorear pedido",
  "Aplicar garantía",
  "¿Qué es Calii?",
  "Aplicar promo",
];

const CANNED_REPLY =
  "Gracias por tu mensaje. Un agente de soporte te atenderá pronto.";

export default function SoporteChat() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Pin the chat container to exactly the remaining viewport height,
  // accounting for the fixed bottom tab bar on mobile (pb-16 = 4rem)
  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;
    const isMobile = () => window.innerWidth < 768;
    const update = () => {
      const top = el.getBoundingClientRect().top;
      const bottomOffset = isMobile() ? "4rem" : "0px";
      el.style.height = `calc(100dvh - ${top}px - ${bottomOffset})`;
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [messages.length > 0]);

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      trackAction("SOPORTE-SEND-MESSAGE", trimmed);
      setPendingMessage(trimmed);
      setInput("");

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    },
    []
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    // Auto-resize up to ~8 lines
    const el = e.target;
    el.style.height = "auto";
    const maxHeight = parseFloat(getComputedStyle(el).lineHeight) * 8;
    if (el.scrollHeight > maxHeight) {
      el.style.height = `${maxHeight}px`;
      el.style.overflow = "auto";
    } else {
      el.style.height = `${el.scrollHeight}px`;
      el.style.overflow = "hidden";
    }
  };

  const inputBar = (
    <div className="flex items-end gap-2">
      <Textarea
        ref={textareaRef}
        value={input}
        onChange={handleTextareaInput}
        onKeyDown={handleKeyDown}
        placeholder="Escribe tu mensaje..."
        rows={1}
        className="flex-1 resize-none overflow-hidden rounded-xl border bg-muted/50 px-4 py-2.5 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
      />
      <Button
        size="icon"
        className="shrink-0 rounded-full"
        disabled={!input.trim()}
        onClick={() => sendMessage(input)}
      >
        <SendHorizontal className="size-4" />
      </Button>
    </div>
  );

  const whatsappDialog = (
    <Dialog open={pendingMessage !== null} onOpenChange={(open) => { if (!open) setPendingMessage(null); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Chat no disponible</DialogTitle>
          <DialogDescription>
            El chat en línea aún no está disponible. Puedes continuar tu conversación por WhatsApp y un agente te atenderá pronto.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            asChild
            className="w-full bg-teal-400 hover:bg-teal-500 text-white"
          >
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(pendingMessage ?? "")}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackAction("SOPORTE-CONTINUE-ON-WHATSAPP", pendingMessage ?? "")}
            >
              Continuar en WhatsApp
            </a>
          </Button>
          <Button variant="outline" className="w-full" onClick={() => { trackAction("SOPORTE-CLOSE-WHATSAPP"); setPendingMessage(null); }}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (messages.length === 0) {
    return (
      <>
      {whatsappDialog}
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-10">
        <h1 className="mb-6 text-2xl font-semibold text-foreground">
          Soporte Calii
        </h1>

        <div className="w-full max-w-xl">{inputBar}</div>

        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {SUGGESTED_PROMPTS.map((prompt) => (
            <Button
              key={prompt}
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => sendMessage(prompt)}
            >
              {prompt}
            </Button>
          ))}
        </div>

        <p className="mt-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <MessageCircle className="size-4" />
            WhatsApp{" "}
            <a href="https://wa.me/5218125707844" className="font-medium underline" target="_blank" rel="noopener noreferrer">
              +52 81 2570 7844
            </a>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Mail className="size-4" />
            <a
              href="mailto:soporte@calii.com"
              className="font-medium underline"
            >
              soporte@calii.com
            </a>
          </span>
        </p>
      </div>
      </>
    );
  }

  return (
    <>
    {whatsappDialog}
    <div ref={chatRef} className="flex flex-col overflow-hidden">
      {/* Contact info banner */}
      <div className="border-b bg-muted/50 px-4 py-2">
        <p className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <MessageCircle className="size-3.5" />
            WhatsApp{" "}
            <a href="https://wa.me/5218125707844" className="font-medium underline" target="_blank" rel="noopener noreferrer">
              +52 81 2570 7844
            </a>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Mail className="size-3.5" />
            <a
              href="mailto:soporte@calii.com"
              className="font-medium underline"
            >
              soporte@calii.com
            </a>
          </span>
        </p>
      </div>

      {/* Messages */}
      <ScrollArea className="min-h-0 flex-1">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-4">
          {messages.map((msg, i) =>
            msg.role === "user" ? (
              <div key={i} className="flex justify-end">
                <div className="max-w-[75%] rounded-2xl bg-primary px-4 py-2 text-sm text-primary-foreground">
                  {msg.content}
                </div>
              </div>
            ) : (
              <div key={i} className="flex items-start gap-2">
                <Avatar className="mt-0.5 size-7">
                  <AvatarFallback className="bg-primary text-[10px] text-primary-foreground">
                    C
                  </AvatarFallback>
                </Avatar>
                <div className="max-w-[75%] rounded-2xl bg-muted px-4 py-2 text-sm">
                  {msg.content}
                </div>
              </div>
            )
          )}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="bg-background px-4 py-5">
        <div className="mx-auto w-full max-w-5xl">{inputBar}</div>
      </div>
    </div>
    </>
  );
}
